import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, MapPin, Phone, Plus, Search, Star, UserPlus, Users } from 'lucide-react';
import { formatMoney, relativeTime, type ChannelType, type Customer } from '@bakery/shared';

import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

const CHANNELS: { value: ChannelType; label: string }[] = [
  { value: 'messenger', label: 'Messenger' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'viber', label: 'Viber' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
];

const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map((c) => [c.value, c.label])) as Record<ChannelType, string>;

export function CustomersScreen() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');
  const [addOpen, setAddOpen] = useState(false);

  const customers = useQuery({
    queryKey: ['customers', search, sort],
    queryFn: () => api.customers.list({ search: search || undefined, sort, limit: 100 }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold">Clients</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
            {customers.data?.total ?? 0} on file, with what they buy and where to reach them.
          </p>
        </div>
        <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setAddOpen(true)}>
          New client
        </Button>
      </header>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, phone, email or @handle…"
            className="pl-9"
          />
        </div>
        <Select value={sort} onChange={(event) => setSort(event.target.value)} className="w-40">
          <option value="recent">Recent</option>
          <option value="name">Name</option>
          <option value="spend">Top spend</option>
        </Select>
      </div>

      {customers.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-[68px]" />
          ))}
        </div>
      ) : customers.data?.customers.length === 0 ? (
        <EmptyState
          icon={<Users className="size-8" />}
          title="No clients yet"
          description="They appear here automatically when you attach someone to a sale, or when a conversation turns into an order."
          action={
            <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setAddOpen(true)}>
              Add the first one
            </Button>
          }
        />
      ) : (
        <ul className="space-y-2">
          {customers.data?.customers.map((customer) => (
            <li key={customer.id}>
              <button
                onClick={() => navigate(`/customers/${customer.id}`)}
                className="card flex w-full items-center gap-4 p-4 text-left transition hover:border-crust-400"
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-accent-soft)] font-display text-sm font-semibold text-[var(--color-accent-ink)]">
                  {customer.displayName.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{customer.displayName}</p>
                  <p className="truncate text-xs text-[var(--color-ink-soft)]">
                    {customer.phone ?? customer.code}
                    {customer.lastOrderAt && ` · last order ${relativeTime(customer.lastOrderAt)}`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular text-sm font-semibold">{formatMoney(customer.totalSpent)}</p>
                  <p className="tabular text-xs text-[var(--color-ink-faint)]">
                    {customer.orderCount} order{customer.orderCount === 1 ? '' : 's'}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {customerId && <CustomerDetail customerId={customerId} onClose={() => navigate('/customers')} />}
      {addOpen && <NewCustomerModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function CustomerDetail({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const profile = useQuery({ queryKey: ['customer', customerId], queryFn: () => api.customers.get(customerId) });
  const orders = useQuery({ queryKey: ['customer-orders', customerId], queryFn: () => api.customers.orders(customerId) });
  const [mode, setMode] = useState<'view' | 'edit' | 'merge' | 'delete'>('view');

  if (!profile.data) {
    return (
      <Modal open onClose={onClose} title="Loading…">
        <Skeleton className="h-64" />
      </Modal>
    );
  }

  const customer = profile.data;

  if (mode === 'edit') {
    return <EditCustomer customer={customer} onDone={() => setMode('view')} />;
  }
  if (mode === 'merge') {
    return <MergeCustomer customer={customer} onDone={() => setMode('view')} />;
  }
  if (mode === 'delete') {
    return <DeleteCustomer customer={customer} onCancel={() => setMode('view')} onDeleted={onClose} />;
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={customer.displayName}
      description={`${customer.code} · ${customer.orderCount} orders · ${formatMoney(customer.totalSpent)} lifetime`}
      footer={
        <>
          <Button variant="ghost" onClick={() => setMode('delete')}>
            Delete
          </Button>
          <Button variant="ghost" onClick={() => setMode('merge')}>
            Merge duplicate
          </Button>
          <Button variant="secondary" onClick={() => setMode('edit')}>
            Edit
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Orders" value={String(customer.orderCount)} />
          <Stat label="Average" value={formatMoney(customer.averageOrderValue)} />
          <Stat label="Open" value={String(customer.openOrderCount)} />
        </div>

        {/* Reach them ---------------------------------------------------- */}
        <section>
          <h3 className="mb-2 text-sm font-semibold">Reach them</h3>
          <div className="flex flex-wrap gap-2">
            {customer.phone && (
              <a
                href={`tel:${customer.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] px-3 py-2 text-sm hover:bg-[var(--color-surface-sunken)]"
              >
                <Phone className="size-3.5" />
                {customer.phone}
              </a>
            )}
            {customer.contactLinks.map((link) => (
              <a
                key={link.channel + link.handle}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] px-3 py-2 text-sm hover:bg-[var(--color-surface-sunken)]"
              >
                <ExternalLink className="size-3.5" />
                {CHANNEL_LABEL[link.channel] ?? link.channel}
              </a>
            ))}
            {customer.contactLinks.length === 0 && !customer.phone && (
              <p className="text-sm text-[var(--color-ink-faint)]">No contact details on file yet.</p>
            )}
          </div>
        </section>

        {customer.addresses.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold">Addresses</h3>
            <ul className="space-y-1.5">
              {customer.addresses.map((address) => (
                <li key={address.id} className="flex items-start gap-2 text-sm">
                  <MapPin className="mt-0.5 size-3.5 shrink-0 text-[var(--color-ink-faint)]" />
                  <span>
                    <span className="font-medium">{address.label}</span> — {address.line1}
                    {address.barangay && `, ${address.barangay}`}, {address.city}
                    {address.landmark && (
                      <span className="block text-xs text-[var(--color-ink-faint)]">Landmark: {address.landmark}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* What they buy ------------------------------------------------- */}
        <div className="grid gap-4 sm:grid-cols-2">
          <section>
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <Star className="size-3.5 text-crust-500" /> Buys most
            </h3>
            {customer.topItems.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-faint)]">Nothing yet.</p>
            ) : (
              <ul className="space-y-1">
                {customer.topItems.slice(0, 5).map((item) => (
                  <li key={item.productId} className="flex justify-between text-sm">
                    <span className="min-w-0 truncate">{item.productName}</span>
                    <span className="tabular shrink-0 pl-2 text-[var(--color-ink-soft)]">{item.totalQty}×</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold">Bought recently</h3>
            {customer.recentItems.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-faint)]">Nothing yet.</p>
            ) : (
              <ul className="space-y-1">
                {customer.recentItems.slice(0, 5).map((item) => (
                  <li key={item.productId} className="flex justify-between text-sm">
                    <span className="min-w-0 truncate">{item.productName}</span>
                    <span className="shrink-0 pl-2 text-xs text-[var(--color-ink-faint)]">
                      {relativeTime(item.lastOrderedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section>
          <h3 className="mb-2 text-sm font-semibold">Order history</h3>
          <ul className="divide-y divide-[var(--color-line)]">
            {orders.data?.slice(0, 10).map((order) => (
              <li key={order.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="tabular font-medium">{order.orderNo}</span>
                <Badge color={order.status.color}>{order.status.name}</Badge>
                <span className="flex-1 truncate text-xs text-[var(--color-ink-faint)]">
                  {relativeTime(order.createdAt)}
                </span>
                <span className="tabular font-medium">{formatMoney(order.total)}</span>
              </li>
            ))}
            {orders.data?.length === 0 && <li className="py-3 text-sm text-[var(--color-ink-faint)]">No orders yet.</li>}
          </ul>
        </section>
      </div>
    </Modal>
  );
}

type Profile = Awaited<ReturnType<typeof api.customers.get>>;

function EditCustomer({ customer, onDone }: { customer: Profile; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    firstName: customer.firstName,
    lastName: customer.lastName ?? '',
    phone: customer.phone ?? '',
    email: customer.email ?? '',
    notes: customer.notes ?? '',
  });

  const save = useMutation({
    mutationFn: () =>
      api.customers.update(customer.id, {
        firstName: form.firstName,
        lastName: form.lastName || null,
        // The display name is what the counter reads, so keep it in step with
        // the parts rather than letting it drift into something older.
        displayName: [form.firstName, form.lastName].filter(Boolean).join(' ').trim(),
        phone: form.phone || null,
        email: form.email || null,
        notes: form.notes || null,
      }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.push({ tone: 'ok', title: `${updated.displayName} saved` });
      onDone();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Modal
      open
      onClose={onDone}
      title={`Edit ${customer.displayName}`}
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.firstName} onClick={() => save.mutate()}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" required>
            <Input value={form.firstName} onChange={set('firstName')} autoFocus />
          </Field>
          <Field label="Last name">
            <Input value={form.lastName} onChange={set('lastName')} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={set('phone')} inputMode="tel" />
          </Field>
          <Field label="Email">
            <Input value={form.email} onChange={set('email')} inputMode="email" />
          </Field>
        </div>
        {/*
          * Addresses and handles, editable here rather than only when the
          * client is first typed in.
          *
          * Anyone who arrived from the Inbox was created from a conversation,
          * which carries a name and a handle and nothing else — so the one
          * screen that could add a delivery address was the one screen they
          * could never go through. A client the shop delivers to had no way to
          * record where.
          */}
        <AddressBook customerId={customer.id} addresses={customer.addresses} />
        <HandleList customerId={customer.id} handles={customer.handles} />

        <Field label="Notes">
          <Textarea rows={4} value={form.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Modal>
  );
}

/** A blank address, and the shape the form edits. */
const emptyAddress = {
  label: 'Home',
  line1: '',
  barangay: '',
  city: 'Taguig',
  landmark: '',
  isDefault: false,
};

type AddressForm = typeof emptyAddress;

function AddressBook({
  customerId,
  addresses,
}: {
  customerId: string;
  addresses: Profile['addresses'];
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  /** The id being edited, 'new' while adding, or null when the list is idle. */
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<AddressForm>(emptyAddress);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: form.label.trim() || 'Home',
        line1: form.line1.trim(),
        barangay: form.barangay.trim() || null,
        city: form.city.trim(),
        landmark: form.landmark.trim() || null,
        isDefault: form.isDefault,
      };
      return editing === 'new'
        ? api.customers.addAddress(customerId, body)
        : api.customers.updateAddress(editing!, body);
    },
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save the address', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: (addressId: string) => api.customers.removeAddress(addressId),
    onSuccess: refresh,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove it', body: error.message }),
  });

  const open = (address?: Profile['addresses'][number]) => {
    setForm(
      address
        ? {
            label: address.label,
            line1: address.line1,
            barangay: address.barangay ?? '',
            city: address.city,
            landmark: address.landmark ?? '',
            isDefault: address.isDefault,
          }
        : emptyAddress,
    );
    setEditing(address?.id ?? 'new');
  };

  const field = (key: keyof AddressForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <section>
      {/*
        * Said plainly, because these save on their own buttons while the name
        * and phone above wait for Save changes. Without the line, adding an
        * address and then pressing Cancel looks like the Cancel did nothing.
        */}
      <h3 className="text-sm font-semibold">Addresses</h3>
      <p className="mb-2 text-xs text-[var(--color-ink-faint)]">Saved as you add them, not on Save changes.</p>

      {addresses.length === 0 && editing === null && (
        <p className="mb-2 text-sm text-[var(--color-ink-faint)]">
          None on file — add one so this client can be delivered to.
        </p>
      )}

      <ul className="mb-2 space-y-1.5">
        {addresses.map((address) => (
          <li key={address.id} className="flex items-start gap-2 text-sm">
            <MapPin className="mt-0.5 size-3.5 shrink-0 text-[var(--color-ink-faint)]" />
            <span className="min-w-0 flex-1">
              <span className="font-medium">{address.label}</span>
              {address.isDefault && <span className="ml-1 text-xs text-[var(--color-ink-faint)]">· default</span>}
              <span className="block text-[var(--color-ink-soft)]">
                {address.line1}
                {address.barangay && `, ${address.barangay}`}, {address.city}
              </span>
            </span>
            <button
              type="button"
              onClick={() => open(address)}
              aria-label={`Edit the ${address.label} address`}
              className="shrink-0 text-xs text-[var(--color-ink-soft)] underline-offset-2 hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => remove.mutate(address.id)}
              aria-label={`Remove the ${address.label} address`}
              className="shrink-0 text-xs text-[var(--color-danger)] underline-offset-2 hover:underline"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>

      {editing === null ? (
        <Button size="sm" icon={<Plus className="size-4" />} onClick={() => open()}>
          Add an address
        </Button>
      ) : (
        <div className="space-y-3 rounded-[var(--radius-md)] border border-[var(--color-line)] p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Label" hint="Home, Office, Mum's place…">
              <Input value={form.label} onChange={field('label')} />
            </Field>
            <Field label="Street" required>
              <Input value={form.line1} onChange={field('line1')} autoFocus />
            </Field>
            <Field label="Barangay">
              <Input value={form.barangay} onChange={field('barangay')} />
            </Field>
            <Field label="City" required>
              <Input value={form.city} onChange={field('city')} />
            </Field>
          </div>
          <Field label="Landmark" hint="What the rider looks for.">
            <Input value={form.landmark} onChange={field('landmark')} />
          </Field>

          {/* Only worth offering once there is more than one to choose between. */}
          {(addresses.length > 0 || editing !== 'new') && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(event) => setForm((current) => ({ ...current, isDefault: event.target.checked }))}
                className="size-4 accent-[var(--color-accent)]"
              />
              Deliver here unless told otherwise
            </label>
          )}

          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              loading={save.isPending}
              disabled={!form.line1.trim() || !form.city.trim()}
              onClick={() => save.mutate()}
            >
              {editing === 'new' ? 'Add address' : 'Save address'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The accounts this person messages from.
 *
 * Worth editing rather than only reading: a handle is what makes an incoming
 * message land on the right record, so adding the one they actually use is how
 * a client stops arriving as a stranger every time.
 */
function HandleList({ customerId, handles }: { customerId: string; handles: Profile['handles'] }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [adding, setAdding] = useState(false);
  const [channel, setChannel] = useState<ChannelType>('messenger');
  const [handle, setHandle] = useState('');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['customer', customerId] });

  const add = useMutation({
    mutationFn: () => api.customers.addHandle(customerId, { channel, handle: handle.trim() }),
    onSuccess: () => {
      setHandle('');
      setAdding(false);
      refresh();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not add it', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: (handleId: string) => api.customers.removeHandle(handleId),
    onSuccess: refresh,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove it', body: error.message }),
  });

  return (
    <section>
      <h3 className="text-sm font-semibold">Social handles</h3>
      <p className="mb-2 text-xs text-[var(--color-ink-faint)]">Saved as you add them, not on Save changes.</p>

      {handles.length === 0 && !adding && (
        <p className="mb-2 text-sm text-[var(--color-ink-faint)]">
          None on file — adding one lets their messages match this record automatically.
        </p>
      )}

      <ul className="mb-2 space-y-1">
        {handles.map((row) => (
          <li key={row.id} className="flex items-center gap-2 text-sm">
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium">{CHANNEL_LABEL[row.channel] ?? row.channel}</span>
              <span className="text-[var(--color-ink-soft)]"> · {row.displayName ?? row.handle}</span>
            </span>
            <button
              type="button"
              onClick={() => remove.mutate(row.id)}
              aria-label={`Remove their ${CHANNEL_LABEL[row.channel] ?? row.channel} handle`}
              className="shrink-0 text-xs text-[var(--color-danger)] underline-offset-2 hover:underline"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>

      {adding ? (
        <div className="flex flex-wrap gap-2">
          <Select value={channel} onChange={(event) => setChannel(event.target.value as ChannelType)} className="w-36">
            {CHANNELS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Input
            value={handle}
            onChange={(event) => setHandle(event.target.value)}
            placeholder="@name or number"
            className="min-w-0 flex-1"
            autoFocus
          />
          <Button size="sm" variant="primary" loading={add.isPending} disabled={!handle.trim()} onClick={() => add.mutate()}>
            Add
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
          Add a handle
        </Button>
      )}
    </section>
  );
}

function MergeCustomer({ customer, onDone }: { customer: Profile; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Customer | null>(null);

  const matches = useQuery({
    queryKey: ['customers', 'merge-search', search],
    queryFn: () => api.customers.list({ search, limit: 8 }),
    enabled: search.trim().length > 1,
  });

  const merge = useMutation({
    mutationFn: () => api.customers.merge(customer.id, chosen!.id),
    onSuccess: (merged) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
      queryClient.invalidateQueries({ queryKey: ['customer-orders', customer.id] });
      toast.push({ tone: 'ok', title: `Merged into ${merged.displayName}`, body: `${merged.orderCount} orders now on this client` });
      onDone();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not merge', body: error.message }),
  });

  const candidates = (matches.data?.customers ?? []).filter((c) => c.id !== customer.id);

  return (
    <Modal
      open
      onClose={onDone}
      title={`Merge into ${customer.displayName}`}
      description="Find the duplicate. Everything on it moves here, and it is then removed."
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button variant="danger" loading={merge.isPending} disabled={!chosen} onClick={() => merge.mutate()}>
            {chosen ? `Merge ${chosen.displayName}` : 'Merge'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Search for the duplicate">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setChosen(null);
            }}
            placeholder="Name or phone number…"
            autoFocus
          />
        </Field>

        {search.trim().length > 1 && candidates.length === 0 && !matches.isLoading && (
          <p className="text-sm text-[var(--color-ink-faint)]">Nobody else matches that.</p>
        )}

        <ul className="space-y-1">
          {candidates.map((candidate) => (
            <li key={candidate.id}>
              <button
                type="button"
                onClick={() => setChosen(candidate)}
                className={cx(
                  'flex w-full items-center justify-between gap-3 rounded-[var(--radius-md)] border px-3 py-2 text-left text-sm',
                  chosen?.id === candidate.id
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                    : 'border-[var(--color-line)] hover:bg-[var(--color-surface-sunken)]',
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{candidate.displayName}</span>
                  <span className="block truncate text-xs text-[var(--color-ink-faint)]">
                    {[candidate.code, candidate.phone].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-[var(--color-ink-faint)]">
                  {candidate.orderCount} order{candidate.orderCount === 1 ? '' : 's'}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {chosen && (
          <div className="rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface-sunken)] p-3 text-sm">
            <p className="font-medium">
              {chosen.displayName} will be removed.
            </p>
            <p className="mt-1 text-[var(--color-ink-soft)]">
              Their orders, conversations, addresses and contact handles move to {customer.displayName}. A phone
              number or email {customer.displayName} is missing will be carried across; anything already filled in
              stays as it is. This cannot be undone.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function DeleteCustomer({
  customer,
  onCancel,
  onDeleted,
}: {
  customer: Profile;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirmation, setConfirmation] = useState('');

  const remove = useMutation({
    mutationFn: () => api.customers.remove(customer.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.push({ tone: 'ok', title: `${customer.displayName} deleted` });
      onDeleted();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not delete', body: error.message }),
  });

  /*
   * Typing the name is not ceremony. The list is full of people who share one,
   * and the row you are looking at came from a search — so the cost of picking
   * the wrong Krizza Martin is somebody else's history, and the confirmation
   * should take a moment to read.
   */
  const confirmed = confirmation.trim().toLowerCase() === customer.displayName.trim().toLowerCase();
  const attached = customer.handles.length + customer.addresses.length;

  return (
    <Modal
      open
      onClose={onCancel}
      title={`Delete ${customer.displayName}?`}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" loading={remove.isPending} disabled={!confirmed} onClick={() => remove.mutate()}>
            Delete for good
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="space-y-1.5 text-sm">
          <li>
            <strong>{customer.orderCount}</strong> order{customer.orderCount === 1 ? '' : 's'} stay in the books, with{' '}
            {customer.orderCount === 1 ? 'its' : 'their'} totals and {customer.displayName}&rsquo;s name on{' '}
            {customer.orderCount === 1 ? 'it' : 'them'} — but no longer linked to anyone.
          </li>
          <li>
            <strong>{attached}</strong> contact detail{attached === 1 ? '' : 's'} — phone handles and addresses — are
            deleted outright.
          </li>
          <li>Any conversation in the Inbox stays, and keeps the name it shows, but is unlinked.</li>
        </ul>

        {customer.openOrderCount > 0 && (
          <div className="rounded-[var(--radius-md)] border border-[var(--color-danger)] p-3 text-sm">
            <p className="font-medium text-[var(--color-danger)]">
              {customer.openOrderCount} order{customer.openOrderCount === 1 ? ' is' : 's are'} still open.
            </p>
            <p className="mt-1 text-[var(--color-ink-soft)]">
              Deleting now removes the phone number and address needed to deliver{' '}
              {customer.openOrderCount === 1 ? 'it' : 'them'}. Finish{' '}
              {customer.openOrderCount === 1 ? 'that order' : 'those orders'} first.
            </p>
          </div>
        )}

        <p className="text-sm text-[var(--color-ink-soft)]">
          If this is a duplicate of someone else, close this and use <strong>Merge duplicate</strong> instead — that
          keeps the orders and contact details rather than throwing them away.
        </p>

        <Field label={`Type “${customer.displayName}” to confirm`}>
          <Input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoFocus />
        </Field>
      </div>
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] px-3 py-2.5">
      <p className="text-xs text-[var(--color-ink-soft)]">{label}</p>
      <p className="tabular mt-0.5 font-display text-lg font-semibold">{value}</p>
    </div>
  );
}

function NewCustomerModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    channel: 'messenger' as ChannelType,
    handle: '',
    line1: '',
    barangay: '',
    city: 'Taguig',
  });

  const create = useMutation({
    mutationFn: () =>
      api.customers.create({
        firstName: form.firstName,
        lastName: form.lastName || null,
        phone: form.phone || null,
        email: form.email || null,
        handles: form.handle ? [{ channel: form.channel, handle: form.handle }] : [],
        addresses: form.line1
          ? [{ label: 'Home', line1: form.line1, barangay: form.barangay || null, city: form.city, isDefault: true }]
          : [],
      }),
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.push({ tone: 'ok', title: `${customer.displayName} added`, body: customer.code });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Modal
      open
      onClose={onClose}
      title="New client"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={create.isPending} disabled={!form.firstName} onClick={() => create.mutate()}>
            Save client
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" required>
            <Input value={form.firstName} onChange={set('firstName')} autoFocus />
          </Field>
          <Field label="Last name">
            <Input value={form.lastName} onChange={set('lastName')} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={set('phone')} placeholder="0917 123 4567" inputMode="tel" />
          </Field>
          <Field label="Email">
            <Input value={form.email} onChange={set('email')} type="email" />
          </Field>
        </div>

        <Field label="Social handle" hint="Lets an incoming message from this person match their record automatically.">
          <div className="flex gap-2">
            <Select value={form.channel} onChange={set('channel')} className="w-36">
              {CHANNELS.map((channel) => (
                <option key={channel.value} value={channel.value}>
                  {channel.label}
                </option>
              ))}
            </Select>
            <Input value={form.handle} onChange={set('handle')} placeholder="@name or number" className="flex-1" />
          </div>
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Address">
            <Input value={form.line1} onChange={set('line1')} placeholder="Street" />
          </Field>
          <Field label="Barangay">
            <Input value={form.barangay} onChange={set('barangay')} />
          </Field>
          <Field label="City">
            <Input value={form.city} onChange={set('city')} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
